import React, { Dispatch, ReactElement, SetStateAction } from 'react';

import i18n from 'asset/translation';
import { ErrorData } from 'domain/types';
import Branding from 'domain/entity/branding';
import { ConstraintsPerLayoutElement, LayoutType } from 'domain/entity/layout';
import Widget, { WidgetType } from 'domain/entity/widget';
import WidgetData, { WidgetDataTypes } from 'domain/entity/widgetData';
import SingleKPIWidgetData from 'domain/entity/widgets/SingleKPIData';
import ChartData from 'domain/entity/widgets/ChartData';
import TableData from 'domain/entity/widgets/TableData';
import GaugeData from 'domain/entity/widgets/GaugeData';
import TextData from 'domain/entity/widgets/TextData';
import ImageData from 'domain/entity/widgets/ImageData';
import HeatmapData from 'domain/entity/widgets/HeatmapData';
import PieData from 'domain/entity/widgets/PieData';
import Pie from 'adapter/in/common/components/widgets/Pie';
import SingleKPIRegular from 'adapter/in/common/components/widgets/SingleKPI/Regular';
import SingleChart from 'adapter/in/common/components/widgets/Chart';
import ErrorWidget from 'adapter/in/common/components/widgets/Error';
import LoadingMessage from 'adapter/in/common/components/widgets/LoadingMessage';
import Heatmap from 'adapter/in/common/components/widgets/Heatmap';
import WidgetText from 'adapter/in/common/components/widgets/Text';
import TextBlockWidget from 'adapter/in/common/components/widgets/TextBlock';
import WidgetImage from 'adapter/in/common/components/widgets/Image';
import WidgetGauge from 'adapter/in/common/components/widgets/Gauge';
import Table from 'adapter/in/common/components/widgets/Table';
import { applyOptions } from 'adapter/in/common/widget/options';
import ErrorBoundary from 'adapter/in/common/components/ErrorBoundary';
import { log } from 'adapter/in/common/config/pino';
import RichTextData from 'domain/entity/widgets/RichTextData';

export type WidgetCriteria = Pick<Widget, 'id' | 'optionFields' | 'type'>;

export const getWidgetBuilder = (
    widgetCriteria: WidgetCriteria,
    data: WidgetDataTypes | Record<string, never>,
    isLoading: boolean,
    branding: Branding,
    decimalSeparator: string,
    thousandSeparator: string,
    dateFormat: string,
    isCurrencyLastPosition: boolean,
    ref: Dispatch<SetStateAction<HTMLDivElement | null>>,
    error?: ErrorData,
    dashboardId?: string,
    isEditEnabled?: boolean
): ((
    width: number,
    height: number,
    layoutType?: LayoutType,
    isOnEdit?: boolean,
    sizeIssue?: ConstraintsPerLayoutElement
) => ReactElement) => (
    width: number,
    height: number,
    layoutType?: LayoutType, // TODO make more abstract? Decision in function of real widget size instead of using layoutType? Or even using isMobile, etc.
    isOnEdit?: boolean,
    sizeIssue?: ConstraintsPerLayoutElement
) => {
    // TODO MOVE???
    if (sizeIssue) {
        const icon = 'warning';
        const errorTitle = i18n.t('widgets.widgetSizeWrongForLayout');
        const errorMessage = i18n.t('widgets.wrongSizeWidgetDescription', {
            minWidth: sizeIssue.minWidth,
            minHeight: sizeIssue.minHeight,
            maxWidth: sizeIssue.maxWidth,
            maxHeight: sizeIssue.maxHeight,
        });
        return (
            <div style={{ width, height }}>
                <ErrorWidget width={width} height={height} errorTitle={errorTitle} errorMessage={errorMessage} icon={icon} />
            </div>
        );
    }

    if (error) {
        const { icon, errorTitle } = getErrorDetails(error.status);

        return (
            <div style={{ width, height }}>
                <ErrorWidget width={width} height={height} errorTitle={errorTitle} errorMessage={error.error.message} icon={icon} />
            </div>
        );
    }

    return (
        <ErrorBoundary onError={log.error}>
            <div ref={ref} style={{ width, height }}>
                {isLoading && <LoadingMessage width={width} height={height} />}
                <div
                    data-testid='blur-effect'
                    style={{
                        padding: '4px',
                        width: 'calc(100% - 8px)',
                        height: 'calc(100% - 8px)',
                        filter: isLoading ? 'blur(0.5em)' : 'blur(0em)',
                        overflow: 'hidden',
                        borderRadius: '4px',
                    }}
                >
                    {Object.keys(data).length > 0 &&
                        widgetCriteria.type !== WidgetType.RICH_TEXT &&
                        buildWidget(
                            data as WidgetData,
                            isLoading,
                            widgetCriteria,
                            decimalSeparator,
                            thousandSeparator,
                            isCurrencyLastPosition,
                            branding,
                            width,
                            height,
                            !!isOnEdit,
                            dateFormat,
                            layoutType ?? LayoutType.DESKTOP
                        )}
                    {widgetCriteria.type === WidgetType.RICH_TEXT && Object.keys(data).length > 0 && (
                        <TextBlockWidget editable={isEditEnabled} dashboardId={dashboardId} textBlockId={widgetCriteria.id} data={data as RichTextData} isLoading={isLoading} />
                    )}
                </div>
            </div>
        </ErrorBoundary>
    );
};

const buildWidget = (
    data: WidgetDataTypes,
    isLoading: boolean,
    widgetCriteria: WidgetCriteria,
    decimalSeparator: string,
    thousandSeparator: string,
    isCurrencyLastPosition: boolean,
    branding: Branding,
    width: number,
    height: number,
    isOnEdit: boolean,
    dateFormat: string,
    layoutType: LayoutType
) => {
    switch (data.type) {
        case WidgetType.MULTI_DEVICE_HEATMAP:
        case WidgetType.HEATMAP:
            return (
                <Heatmap
                    data={applyOptions(data, widgetCriteria.optionFields) as HeatmapData}
                    decimalSeparator={decimalSeparator}
                    thousandSeparator={thousandSeparator}
                    width={width}
                    isOnEdit={isOnEdit}
                    height={height}
                    key={widgetCriteria.id}
                    branding={branding}
                    isLoading={isLoading}
                    dateFormat={dateFormat}
                />
            );
        case WidgetType.SINGLE_KPI:
            return (
                <SingleKPIRegular
                    key={widgetCriteria.id}
                    width={width}
                    height={height}
                    isOnEdit={isOnEdit}
                    decimalSeparator={decimalSeparator}
                    thousandSeparator={thousandSeparator}
                    isCurrencyLastPosition={isCurrencyLastPosition}
                    data={applyOptions(data, widgetCriteria.optionFields) as SingleKPIWidgetData}
                    dateFormat={dateFormat}
                    branding={branding}
                    isLoading={isLoading}
                    showDataAvailability={layoutType === LayoutType.DESKTOP && (data as SingleKPIWidgetData).dataAvailability?.enabled}
                />
            );
        case WidgetType.BAR:
        case WidgetType.LINE:
        case WidgetType.STACKED_BAR:
            return (
                <SingleChart
                    data={applyOptions(data, widgetCriteria.optionFields) as ChartData}
                    decimalSeparator={decimalSeparator}
                    thousandSeparator={thousandSeparator}
                    dateFormat={dateFormat}
                    width={width}
                    isOnEdit={isOnEdit}
                    height={height}
                    key={widgetCriteria.id}
                    branding={branding}
                    isLoading={isLoading}
                    widgetType={data.type}
                    showLegend={layoutType !== LayoutType.MOBILE}
                />
            );
        case WidgetType.PIE:
            return (
                <Pie
                    data={applyOptions(data, widgetCriteria.optionFields) as PieData}
                    decimalSeparator={decimalSeparator}
                    thousandSeparator={thousandSeparator}
                    width={width}
                    isOnEdit={isOnEdit}
                    height={height}
                    key={widgetCriteria.id}
                    branding={branding}
                    isLoading={isLoading}
                    showLegend={layoutType !== LayoutType.MOBILE}
                    showDataAvailability={layoutType === LayoutType.DESKTOP && (data as PieData).dataAvailability?.enabled}
                    dateFormat={dateFormat}
                />
            );
        case WidgetType.TEXT:
            return <WidgetText height={height} data={applyOptions(data, widgetCriteria.optionFields) as TextData} isLoading={isLoading} />;
        case WidgetType.IMAGE:
            return <WidgetImage height={height} width={width} data={data as ImageData} isLoading={isLoading} />;
        case WidgetType.GAUGE:
            return (
                <WidgetGauge
                    data={applyOptions(data, widgetCriteria.optionFields) as GaugeData}
                    isOnEdit={isOnEdit}
                    decimalSeparator={decimalSeparator}
                    thousandSeparator={thousandSeparator}
                    dateFormat={dateFormat}
                    width={width}
                    height={height}
                    key={widgetCriteria.id}
                    branding={branding}
                    isLoading={isLoading}
                    showDataAvailability={layoutType === LayoutType.DESKTOP && (data as GaugeData).dataAvailability?.enabled}
                />
            );
        case WidgetType.TABLE:
            return (
                <Table
                    width={width}
                    decimalSeparator={decimalSeparator}
                    thousandSeparator={thousandSeparator}
                    dateFormat={dateFormat}
                    layoutType={layoutType}
                    isCurrencyLastPosition={isCurrencyLastPosition}
                    data={applyOptions(data, widgetCriteria.optionFields) as TableData}
                    isLoading={isLoading}
                    branding={branding}
                    key={widgetCriteria.id}
                    keyAsProp={widgetCriteria.id}
                />
            );
        default:
            return (
                <ErrorWidget
                    width={width}
                    height={height}
                    errorMessage={i18n.t('widgets.unknownWidgetType')}
                    icon='error'
                    errorTitle={i18n.t('widgets.genericError')}
                />
            );
    }
};

const getErrorDetails = (status: number): { errorTitle: string; icon: string } => {
    switch (status) {
        case 409:
            return { errorTitle: i18n.t('widgets.error409'), icon: 'warning' };
        case 403:
            return { errorTitle: i18n.t('widgets.error403'), icon: 'block' };
        case 500:
        default:
            return { errorTitle: i18n.t('widgets.genericError'), icon: 'error' };
    }
};
